import { useMutation } from '@apollo/client';
import React, { useContext } from 'react';
import { PurchaseRequirement } from 'graphql/entities';
import { useBackgroundProcess } from 'utils/BackgroundJobs';
import RaisePurchaseOrdersFromPurchaseRequirements from 'graphql/mutations/purchases/RaisePurchaseOrdersFromPurchaseRequirements.graphql';
import { serviceActions } from '../../state/modules/service';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { useDispatch } from 'react-redux';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import LinkNotification from '../../components/Notification/LinkNotification';
import { ActionButton } from '../../components/connectedForm/Button/Button';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useActionProps } from '../../utils/useActionProps';
import { AlertType } from '../../graphql/schema/p4types';
import { useP4ToastController } from '../../views/system/NotificationsArea';

const RaisePurchaseOrdersAction = () => {
  const workspaceContext = useContext(WorkspaceContext);
  const dispatch = useDispatch();
  const actionProps = useActionProps('purchaseRequirements');

  const variables = {
    input: {
      clientMutationId: getClientMutationId(),
      ...normalizeVariables({
        ...actionProps,
        purchaseRequirements: actionProps.purchaseRequirements.filter((pr) => pr.id !== 'ALL'), //Filter out custom row.
      }),
    },
  };

  const includedNoSupplier = variables.input.purchaseRequirements.find((purchaseRequirement) => {
    return purchaseRequirement.supplier === null;
  });

  const [raisePurchaseOrders] = useMutation(RaisePurchaseOrdersFromPurchaseRequirements, {
    onError: (data) =>
      handleComponentQueryError(data, {
        dispatch,
        entity: PurchaseRequirement,
        name: 'Could not raise purchase orders',
      }),
  });

  const { dispatchToast, dismissToast } = useP4ToastController();

  const runBackgroundProcess = useBackgroundProcess({
    title: 'Raise Purchase Orders',
    onComplete: (job) => {
      if (job.recordsProcessed > 0) {
        workspaceContext.reloadData?.(true);

        const raisePurchaseOrdersToastId = `${job.jobId}-result`;

        dispatchToast(
          <LinkNotification
            job={job}
            type={AlertType.Success}
            title={`${job.recordsProcessed} orders have been raised`}
            link={`/Purchasing/Order/Workspace/${job.jobId}`}
            linkTitle={'View Orders'}
            onClose={() => {
              dismissToast(raisePurchaseOrdersToastId);
            }}
          />,
          {
            title: `${job.recordsProcessed} orders have been raised`,
            type: AlertType.Action,
            intent: 'success',
            toastId: raisePurchaseOrdersToastId,
          }
        );
      } else {
        dispatch(
          serviceActions.genericAlert(
            'No Purchase Orders Raised',
            'No purchase orders could be raised from the supplied criteria',
            AlertType.Warning
          )
        );
      }
    },
  });

  const onClick = () => {
    if (includedNoSupplier && !workspaceContext.selectionState?.selectedAll) {
      dispatch(
        serviceActions.genericAlert(
          'No Purchase Orders Raised',
          'Unable to raise purchase orders for shortages with no supplier',
          AlertType.Warning
        )
      );
      return;
    }
    raisePurchaseOrders({
      variables,
    }).then((result) => {
      const key = Object.keys(result.data)[0];
      runBackgroundProcess(result.data[key].result);
    });
  };

  return <ActionButton label={'Raise Purchase Orders'} onClick={onClick} />;
};

export const workspace = [
  {
    description: 'Generation',
    url: '/Purchasing/ShortageGeneration/Criteria',
    ignoreState: true,
    actionReference: 'SHORTAGES_GENERATION',
    ignoreBulkActionCheck: true,
  },
  {
    description: 'Raise Purchase Orders',
    element: <RaisePurchaseOrdersAction />,
  },
];
